import { agentInvitationApi } from "@/api/AgentInvitationApi";
import {
  AgentAlreadyLinkedToPropertyError,
  AgentInvitationAlreadyProcessedError,
  AgentInvitationExpiredError,
  AgentInvitationNotFoundError,
  BadRequestError,
  LandlordCannotInviteSelfError,
  NotFoundError,
  TooManyRequestsError,
} from "@/api/Errors";
import { landlordApi } from "@/api/LandlordApi";
import { userApi } from "@/api/UserApi";
import { ErrorView } from "@/components/ui/ErrorView";
import { LoadingDots } from "@/components/ui/LoadingDots";
import { Logo } from "@/components/ui/Logo";
import { Button } from "@/components/ui/shadcn/button";
import { useAuth } from "@/components/contexts/AuthContext/UseAuth";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { toast } from "sonner";
import { UserType } from "@/types/IdentityTypes";

const AgentInvitePage = () => {
  const { invitationId } = useParams();
  const { isAuthenticated, login } = useAuth();

  if (!invitationId) {
    return (
      <div className="flex items-center justify-center h-screen">
        <ErrorView description="Invalid invitation link" />
      </div>
    );
  }

  if (!isAuthenticated) {
    // Hint Agent so corporate brokers land on agent signup. Freelance brokers
    // whose personal email is rejected can fall back to regular signup (per
    // #917); the auto-switch below flips them to Landlord on return.
    login(`/agent-invite/${invitationId}`, UserType.Agent);
    return null;
  }

  return <AgentInviteComponent invitationId={invitationId} />;
};

const AgentInviteComponent = ({ invitationId }: { invitationId: string }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user, setUserType } = useAuth();
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSwitchingRole, setIsSwitchingRole] = useState(false);
  const [switchError, setSwitchError] = useState(false);
  const switchAttempted = useRef(false);

  const userType = user?.profile?.UserType;
  const isAgent =
    userType === UserType.Agent || userType === UserType.AgencyManager;
  const isManager = userType === UserType.Manager;

  // For non-agent recipients we always materialize the Core User + Landlord
  // rows (sync is idempotent) and, if needed, flip the identity-server role
  // to Manager so they land on the manager experience after accepting.
  // Agents go through their own path and skip both.
  useEffect(() => {
    if (isAgent || switchAttempted.current) return;
    switchAttempted.current = true;

    const prepareManager = async () => {
      setIsSwitchingRole(true);
      try {
        if (!isManager) {
          await userApi.setUserRole("Manager");
          await setUserType(UserType.Manager);
        }
        // Sync is idempotent — safe to run for users who were already
        // Landlord (creates Core User/Landlord rows on first call, no-op
        // afterwards). Without this, the accept handler can't find the
        // user's Core User row and the request 422s.
        await landlordApi.syncProfileFromIdentity();
      } catch {
        setSwitchError(true);
      } finally {
        setIsSwitchingRole(false);
      }
    };

    prepareManager();
  }, [isAgent, isManager, setUserType]);

  const {
    data: invitationDetails,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["agentInvitationDetails", invitationId],
    queryFn: () => agentInvitationApi.getInvitationDetails(invitationId),
    staleTime: Infinity,
    enabled: !isSwitchingRole && !switchError,
    retry: (failureCount, error) => {
      // ApiBase maps 4xx responses to these typed errors — no point retrying
      // them since the outcome won't change.
      if (
        error instanceof BadRequestError ||
        error instanceof NotFoundError ||
        error instanceof TooManyRequestsError ||
        error instanceof AgentInvitationNotFoundError ||
        error instanceof AgentInvitationExpiredError
      ) {
        return false;
      }
      return failureCount < 3;
    },
  });

  const { mutate: processInvitation } = useMutation({
    mutationFn: (isApproved: boolean) =>
      agentInvitationApi.processInvitation(invitationId, { isApproved }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["agent", "properties"] });
      queryClient.invalidateQueries({ queryKey: ["properties"] });
      queryClient.invalidateQueries({ queryKey: ["landlord-properties"] });
      navigate(isAgent ? "/agent/properties" : "/manager/properties");
    },
    onError: (error) => {
      if (error instanceof AgentAlreadyLinkedToPropertyError) {
        navigate(isAgent ? "/agent/properties" : "/manager/properties");
        return;
      }
      if (
        error instanceof AgentInvitationAlreadyProcessedError ||
        error instanceof AgentInvitationExpiredError ||
        error instanceof AgentInvitationNotFoundError ||
        error instanceof LandlordCannotInviteSelfError
      ) {
        toast.error(error.message);
      } else {
        toast.error("Failed to process invitation. Please try again.");
      }
      setIsProcessing(false);
    },
  });

  const handleApprove = () => {
    setIsProcessing(true);
    processInvitation(true);
  };

  const handleReject = () => {
    setIsProcessing(true);
    processInvitation(false);
  };

  if (isSwitchingRole) {
    return (
      <div className="flex flex-col items-center justify-center h-screen">
        <div className="w-full flex items-center justify-center h-[70px]">
          <Logo variant="A" className="w-[100px]" />
        </div>
        <div className="flex flex-1 items-center justify-center gap-4">
          <h1 className="text-lg font-semibold text-gray-600">
            Switching to manager experience
          </h1>
          <LoadingDots className="scale-150 translate-y-1" />
        </div>
      </div>
    );
  }

  if (switchError) {
    return (
      <div className="flex items-center justify-center h-screen">
        <ErrorView
          title="Something went wrong"
          description="Failed to switch to the manager experience. Please try again."
        />
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex flex-col items-center h-screen">
        <div className="w-full flex items-center justify-center h-[70px]">
          <Logo variant="A" className="w-[100px]" />
        </div>
        <div className="flex flex-1 items-center justify-center gap-4">
          <h1 className="text-2xl font-bold">Loading invitation</h1>
          <LoadingDots className="scale-150 translate-y-1" />
        </div>
      </div>
    );
  }

  if (error) {
    return <InvitationErrorScreen error={error} />;
  }

  if (!invitationDetails) {
    return <InvitationErrorScreen error={new AgentInvitationNotFoundError()} />;
  }

  return (
    <div className="flex flex-col min-h-screen bg-gradient-to-b from-white via-gray-50/30 to-gray-50">
      {/* Logo */}
      <div className="flex justify-center pt-8 pb-4">
        <Logo variant="A" className="w-[120px]" />
      </div>

      {/* Content */}
      <div className="flex-1 flex items-center justify-center px-4 py-8">
        <div className="max-w-2xl w-full bg-white rounded-lg shadow-lg p-8">
          <h1 className="text-3xl font-bold mb-2">
            Property Management Invitation
          </h1>
          <p className="text-gray-600 mb-8">
            You have been invited to manage a property
          </p>

          {/* Property Details */}
          <div className="mb-6">
            <h2 className="text-xl font-semibold mb-4">Property Details</h2>
            <div className="space-y-2">
              <div>
                <span className="font-medium">Address: </span>
                <span>{invitationDetails.propertyAddress}</span>
              </div>
              <div>
                <span className="font-medium">Type: </span>
                <span>{invitationDetails.propertyType}</span>
              </div>
            </div>
          </div>

          {/* Landlord Details */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold mb-4">Landlord Information</h2>
            <div className="space-y-2">
              <div>
                <span className="font-medium">Name: </span>
                <span>{invitationDetails.landlordName}</span>
              </div>
              {invitationDetails.landlordEmail && (
                <div>
                  <span className="font-medium">Email: </span>
                  <span>{invitationDetails.landlordEmail}</span>
                </div>
              )}
              {invitationDetails.landlordPhoneNumber && (
                <div>
                  <span className="font-medium">Phone: </span>
                  <span>{invitationDetails.landlordPhoneNumber}</span>
                </div>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-4">
            <Button
              onClick={handleApprove}
              disabled={isProcessing}
              className="flex-1"
              size="lg"
            >
              {isProcessing ? (
                <>
                  <LoadingDots color="current" className="mr-2" />
                  Processing...
                </>
              ) : (
                "Accept Invitation"
              )}
            </Button>
            <Button
              onClick={handleReject}
              disabled={isProcessing}
              variant="outline"
              className="flex-1"
              size="lg"
            >
              Decline
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Renders a tailored error state when the invitation can't be loaded.
 *
 * The backend deliberately collapses "wrong recipient" into
 * AgentInvitationNotFound to avoid leaking whether a given reference exists
 * for someone else (see GetAgentInvitationDetailsEndpoint.IsCallerTheInvitee).
 * So when we see NotFound, the most likely cause for a real link the user
 * just clicked is that they're signed in with a different account than the
 * one the invite was addressed to — surface that path first.
 */
const InvitationErrorScreen = ({ error }: { error: Error }) => {
  const { user, logout } = useAuth();
  const signedInEmail = user?.profile?.email;

  const isExpired = error instanceof AgentInvitationExpiredError;

  if (isExpired) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen px-4 py-8">
        <ErrorView
          title="This invitation has expired"
          description="Ask the landlord to send you a new invitation link."
        />
        <Button asChild variant="outline" className="mt-2">
          <Link to="/">Go to home</Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-screen px-4 py-8">
      <ErrorView
        title="We couldn't open this invitation"
        description={
          signedInEmail
            ? `This link may have been sent to a different email, or it's no longer valid. You're signed in as ${signedInEmail} — if the invitation was sent to a different address, sign out and sign back in with that account.`
            : "This link may have been sent to a different email, or it's no longer valid. If the invitation was sent to a different account, sign out and sign back in with that account."
        }
      />
      <div className="mt-2 flex gap-2">
        <Button variant="outline" onClick={() => logout()}>
          Sign out
        </Button>
        <Button asChild variant="outline">
          <Link to="/">Go to home</Link>
        </Button>
      </div>
    </div>
  );
};

export default AgentInvitePage;
